import React from "react";
import { Typography, Stack, Alert, Box, alpha } from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import RefreshIcon from "@mui/icons-material/Refresh";
import ReplayIcon from "@mui/icons-material/Replay";

import AppModal from "../common/AppModal";
import AppButton from "../buttons/AppButton";
import SubmitButton from "../buttons/SubmitButton";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { revalidateLicense } from "../../redux/actions/appStatusActions";
import { ApiResponseStatus } from "../../shared/enums/ApiResponseStatus";

const GlobalLicenseModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const { licenseModal, loadingLicenseRevalidate } = useAppSelector(
    (state) => state.appStatus,
  );

  if (!licenseModal.isOpen) return null;

  const isInvalid = licenseModal.status === ApiResponseStatus.LICENSE_INVALID;
  const canRevalidate =
    licenseModal.status === ApiResponseStatus.LICENSE_EXPIRED ||
    licenseModal.status === ApiResponseStatus.LICENSE_FAILED_VALIDATION;

  return (
    <AppModal
      open={licenseModal.isOpen}
      onClose={() => {}} // Non-closable backdrop: requires license resolution
      title={isInvalid ? "License Invalid" : "License Validation Alert"}
      width="xs"
      showCloseIcon={false}
      loading={loadingLicenseRevalidate}
    >
      <Stack
        spacing={2.5}
        sx={{
          py: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Centered Circular Icon Badge */}
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "50%",
            bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "error.main",
          }}
        >
          <LockOutlinedIcon sx={{ fontSize: 36 }} />
        </Box>

        {/* Title */}
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {isInvalid ? "Authorization Denied" : "Validation Required"}
          </Typography>
        </Box>

        {/* Server Alert Message */}
        <Alert
          severity={isInvalid ? "error" : "warning"}
          sx={{
            width: "100%",
            textAlign: "center",
            justifyContent: "center",
            "& .MuiAlert-message": { width: "100%", textAlign: "center" },
          }}
        >
          {licenseModal.message}
        </Alert>

        {/* Helper Explanations */}
        {isInvalid ? (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", px: 1, lineHeight: 1.6 }}
          >
            Your installation license key is invalid or unrecognized by the
            licensing authority. Please contact technical support immediately to
            restore service.
          </Typography>
        ) : (
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", px: 1, lineHeight: 1.6 }}
          >
            Please verify your network connection or click below to trigger a
            live re-validation check with the central license server.
          </Typography>
        )}

        {/* Centered Action Buttons */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            width: "100%",
            pt: 1,
          }}
        >
          {canRevalidate && (
            <SubmitButton
              title="Re-validate License"
              startIcon={<RefreshIcon />}
              loading={loadingLicenseRevalidate}
              disabled={loadingLicenseRevalidate}
              onClick={() => dispatch(revalidateLicense())}
            />
          )}

          {isInvalid && (
            <AppButton
              variant="outlined"
              color="error"
              startIcon={<ReplayIcon />}
              onClick={() => window.location.reload()}
            >
              Reload Window
            </AppButton>
          )}
        </Box>
      </Stack>
    </AppModal>
  );
};

export default GlobalLicenseModal;
